"use client";

/** The landing page's marks — OUTLINES (owner 2026-10-03: "remove colors just a stroke like the
 *  card shape", then "make them yellow … in home page"): the card's own shape (rounded-lg, the
 *  hairline `border-border`, the `bg-1` fill) with the figure as one line weight in the brand
 *  yellow (`.mark-yellow`, each theme's own --yellow). The numbered steps use the same box
 *  (`IconStep`). `OutlineMark` is shared with `MasterAvatar`.
 *  Inline SVG, nothing fetched. */

/** The card-shaped outline box with a 24-unit line figure inside. */
export function OutlineMark({
  size = 44,
  tone = "ink",
  children,
}: {
  size?: number;
  /** "yellow" — the brand yellow (`.mark-yellow`); "ink" — the text colour. */
  tone?: "ink" | "yellow";
  children: React.ReactNode;
}) {
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-lg border border-border bg-bg-1 ${
        tone === "yellow" ? "mark-yellow" : "text-primary"
      }`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg
        viewBox="0 0 24 24"
        width={Math.round(size * 0.58)}
        height={Math.round(size * 0.58)}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </svg>
    </span>
  );
}



/** Three master styles — three candles, each on a longer horizon than the last. */
export function IconTimeframes({ size = 44 }: { size?: number }) {
  return (
    <OutlineMark size={size} tone="yellow">
      <path d="M6 5.5V8M6 16v3M12 3v2M12 14v5.5M18 6v2M18 18v2" />
      <rect x="4" y="8" width="4" height="8" rx=".8" />
      <rect x="10" y="5" width="4" height="9" rx=".8" />
      <rect x="16" y="8" width="4" height="10" rx=".8" />
    </OutlineMark>
  );
}

/** Non-custodial — a trade-only key: the ring is closed, the bit is cut once. */
export function IconKey({ size = 44 }: { size?: number }) {
  return (
    <OutlineMark size={size} tone="yellow">
      <circle cx="8" cy="12" r="4" />
      <circle cx="8" cy="12" r="1.2" />
      <path d="M12 12h9M18 12v3M20.5 12v2" />
    </OutlineMark>
  );
}

/** A numbered step — the pipeline's 1·2·3·4 — in the same card-shaped outline as every other
 *  home mark, the numeral in the yellow (owner 2026-10-03, shown the steps beside the outline
 *  marks: "do the same"; it replaces the 09-25 yellow coin). */
export function IconStep({ n, size = 26 }: { n: number; size?: number }) {
  return (
    <span
      className="mark-yellow flex shrink-0 items-center justify-center rounded-lg border border-border bg-bg-1 font-mono font-bold"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }}
      aria-hidden
    >
      {n}
    </span>
  );
}

/** Owned decision brain — a chip, not a cloud: the policy runs here. */
export function IconChip({ size = 44 }: { size?: number }) {
  return (
    <OutlineMark size={size} tone="yellow">
      <rect x="6.5" y="6.5" width="11" height="11" rx="2" />
      <rect x="10" y="10" width="4" height="4" rx=".8" />
      <path d="M9.5 3.5v3M14.5 3.5v3M9.5 17.5v3M14.5 17.5v3M3.5 9.5h3M3.5 14.5h3M17.5 9.5h3M17.5 14.5h3" />
    </OutlineMark>
  );
}

/** SPOT (2026-10-11): a price falling in steps and a buy mark at each lower level — buying
 *  the dips, the fourth feature card beside the three copy-trading ones. */
export function IconDips({ size = 44 }: { size?: number }) {
  return (
    <OutlineMark size={size} tone="yellow">
      <path d="M4 6l5 5 3-2 5 6 4-3" />
      <path d="M9 11v3M17 15v3M21 12v3" />
      <path d="M7.5 15.5h3M15.5 19.5h3M19.5 16.5h3" />
    </OutlineMark>
  );
}
